import { Study, ApiResponse, PaginatedResponse } from '@/types';
import { http } from '@/services/api';

/**
 * Service for managing biblical studies
 */
class StudiesService {
  private isLaravelPagination(
    obj: unknown
  ): obj is { data: Study[]; total: number | string; per_page?: number | string; current_page?: number | string } {
    if (typeof obj !== 'object' || obj === null) return false;
    const rec = obj as Record<string, unknown>;
    return Array.isArray(rec.data) && rec.total !== undefined;
  }

  /**
   * Update a scheduled study entry (study-schedules)
   */
  async updateSchedule(
    id: number,
    data: {
      scheduled_date?: string;
      scheduled_time?: string;
      description?: string;
      status?: string;
      notes?: string | null;
    }
  ): Promise<ApiResponse<boolean>> {
    try {
      await http.put<void>(`/study-schedules/${id}`, data);
      return { data: true, success: true, message: 'Agendamento atualizado com sucesso' };
    } catch (error) {
      console.error('Error updating schedule:', error);
      return { data: false, success: false, message: 'Erro ao atualizar agendamento' };
    }
  }

  /**
   * Delete a scheduled study entry (study-schedules)
   */
  async deleteSchedule(id: number): Promise<ApiResponse<boolean>> {
    try {
      await http.delete<void>(`/study-schedules/${id}`);
      return { data: true, success: true, message: 'Agendamento excluído com sucesso' };
    } catch (error) {
      console.error('Error deleting schedule:', error);
      return { data: false, success: false, message: 'Erro ao excluir agendamento' };
    }
  }

  /**
   * Get all studies
   */
  async getStudies(): Promise<ApiResponse<Study[]>> {
    try {
      const res = await http.get<unknown>('/studies');
      // Normalize: accept array or { data: Study[] }
      let list: Study[] = [];
      if (Array.isArray(res)) {
        list = res as Study[];
      } else if (typeof res === 'object' && res !== null && Array.isArray((res as any).data)) {
        list = (res as any).data as Study[];
      }
      return { data: list, success: true };
    } catch (error) {
      console.error('Error getting studies:', error);
      return {
        data: [],
        success: false,
        message: 'Erro ao obter estudos'
      };
    }
  }

  /**
   * Get paginated studies
   */
  async getPaginatedStudies(
    page: number = 1,
    limit: number = 10,
    search?: string
  ): Promise<ApiResponse<PaginatedResponse<Study>>> {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);

      const res = await http.get<unknown>(`/studies?${params.toString()}`);

      // Handle common Laravel pagination shapes or plain array fallback
      if (Array.isArray(res)) {
        const total = res.length;
        const totalPages = Math.ceil(total / limit);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const data = res.slice(startIndex, endIndex);
        return { data: { data, total, page, limit, totalPages }, success: true };
      }

      if (this.isLaravelPagination(res)) {
        const r = res;
        const perPage = Number(r.per_page ?? limit);
        const totalPages = Math.ceil(Number(r.total) / perPage);
        return {
          data: {
            data: r.data,
            total: Number(r.total),
            page: Number(r.current_page ?? page),
            limit: perPage,
            totalPages,
          },
          success: true,
        };
      }

      // Fallback: treat as list
      const list: Study[] =
        typeof res === 'object' && res !== null && Array.isArray((res as Record<string, unknown>).data)
          ? ((res as Record<string, unknown>).data as Study[])
          : [];
      return {
        data: {
          data: list,
          total: list.length,
          page,
          limit,
          totalPages: Math.ceil(list.length / limit),
        },
        success: true,
      };
    } catch (error) {
      console.error('Error getting paginated studies:', error);
      return {
        data: {
          data: [],
          total: 0,
          page,
          limit,
          totalPages: 0
        },
        success: false,
        message: 'Erro ao obter estudos paginados'
      };
    }
  }

  /**
   * Get study by ID
   */
  async getStudyById(id: number): Promise<ApiResponse<Study | null>> {
    try {
      const study = await http.get<Study>(`/studies/${id}`);
      return { data: study, success: true };
    } catch (error) {
      console.error('Error getting study by ID:', error);
      return {
        data: null,
        success: false,
        message: 'Erro ao obter estudo'
      };
    }
  }

  /**
   * Create a new study
   */
  async createStudy(studyData: Omit<Study, 'id' | 'created_at'>): Promise<ApiResponse<Study>> {
    try {
      const created = await http.post<Study>('/studies', studyData);
      return { data: created, success: true, message: 'Estudo criado com sucesso' };
    } catch (error) {
      console.error('Error creating study:', error);
      return {
        data: {} as Study,
        success: false,
        message: 'Erro ao criar estudo'
      };
    }
  }

  /**
   * Update an existing study
   */
  async updateStudy(id: number, studyData: Partial<Study>): Promise<ApiResponse<Study>> {
    try {
      const updated = await http.put<Study>(`/studies/${id}`, studyData);
      return { data: updated, success: true, message: 'Estudo atualizado com sucesso' };
    } catch (error) {
      console.error('Error updating study:', error);
      return {
        data: {} as Study,
        success: false,
        message: 'Erro ao atualizar estudo'
      };
    }
  }

  /**
   * Delete a study (soft delete by setting active to false)
   */
  async deleteStudy(id: number): Promise<ApiResponse<boolean>> {
    try {
      await http.delete<void>(`/studies/${id}`);
      return { data: true, success: true, message: 'Estudo excluído com sucesso' };
    } catch (error) {
      console.error('Error deleting study:', error);
      return {
        data: false,
        success: false,
        message: 'Erro ao excluir estudo'
      };
    }
  }

  /**
   * Permanently delete a study
   */
  async permanentlyDeleteStudy(id: number): Promise<ApiResponse<boolean>> {
    try {
      // Backend destroy is permanent by default
      await http.delete<void>(`/studies/${id}`);
      return { data: true, success: true, message: 'Estudo excluído permanentemente' };
    } catch (error) {
      console.error('Error permanently deleting study:', error);
      return {
        data: false,
        success: false,
        message: 'Erro ao excluir estudo permanentemente'
      };
    }
  }

  /**
   * Get upcoming scheduled studies (now uses /study-schedules/upcoming, falls back to /study-schedules with filters)
   */
  async getUpcomingStudies(params?: {
    startDate?: string; // YYYY-MM-DD
    endDate?: string;   // YYYY-MM-DD
    limit?: number;     // 1..50
  }): Promise<ApiResponse<Study[]>> {
    const startDate = params?.startDate;
    const endDate = params?.endDate;
    const limit = params?.limit ?? 20;
    // First try the dedicated endpoint
    try {
      const usp = new URLSearchParams();
      if (startDate) usp.set('start_date', startDate);
      if (endDate) usp.set('end_date', endDate);
      usp.set('limit', String(Math.min(Math.max(limit, 1), 50)));
      const res = await http.get<{ data: Study[] } | Study[]>(`/study-schedules/upcoming?${usp.toString()}`);
      const data = Array.isArray(res) ? res : res?.data ?? [];
      return { data, success: true };
    } catch (err) {
      console.warn('getUpcomingStudies: falling back to /study-schedules with filters', err);
      // Fallback to index with filters (date range only)
      try {
        const usp = new URLSearchParams();
        if (startDate) usp.set('start_date', startDate);
        if (endDate) usp.set('end_date', endDate);
        usp.set('per_page', String(limit));
        usp.set('sort_by', 'study_date');
        usp.set('sort_dir', 'asc');
        const res = await http.get<unknown>(`/study-schedules?${usp.toString()}`);
        if (Array.isArray(res)) {
          return { data: res as Study[], success: true };
        }
        const list: Study[] =
          typeof res === 'object' && res !== null && Array.isArray((res as any).data)
            ? ((res as any).data as Study[])
            : [];
        return { data: list, success: true };
      } catch (error) {
        console.error('Error getting upcoming studies (fallback):', error);
        return { data: [], success: false, message: 'Erro ao obter estudos agendados' };
      }
    }
  }

  /**
   * Get statistics for scheduled studies (study-schedules)
   * Normalizes backend keys like byStatus/by_type to a consistent shape
   */
  async getSchedulesStatistics(): Promise<{
    total: number;
    by_status: Record<string, number>;
    by_type?: Record<string, number>;
    metrics?: Record<string, unknown>;
  }> {
    type StatsShape = {
      total?: number;
      by_status?: Record<string, number>;
      byStatus?: Record<string, number>;
      by_type?: Record<string, number>;
      byType?: Record<string, number>;
      metrics?: Record<string, unknown>;
    };

    async function fetchStats(url: string): Promise<StatsShape> {
      const raw = await http.get<unknown>(url);
      const obj = (raw as { data?: unknown })?.data ?? raw;
      return (obj as StatsShape) ?? {};
    }

    let data: StatsShape = {};
    try {
      data = await fetchStats('/study-schedules/statistics');
    } catch {
      // fallback to studies statistics if schedules stats is not present
      data = await fetchStats('/studies/statistics');
    }

    const total = Number(data.total ?? 0);
    const by_status: Record<string, number> = data.by_status ?? data.byStatus ?? {};
    const by_type: Record<string, number> | undefined = data.by_type ?? data.byType ?? undefined;
    const metrics: Record<string, unknown> | undefined = data.metrics ?? undefined;
    return { total, by_status, by_type, metrics };
  }
}

export const studiesService = new StudiesService();
export default studiesService;
